import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import type { Field } from './Fields/types'
import type { Entity } from './types'
import { useEntity } from './useEntity'

/**
 * Manages UI state for entities
 * - sidebar
 * - file preview
 * - etc
 */
export const useEntityUIStore = defineStore('entityUI', () => {
  const entityStore = useEntity()

  const selectedFileField: Ref<{
    field: Field<'file' | 'file_collection'>
    groundingField?: Field
  } | null> = ref(null)

  const selectedEntityId = computed(() => entityStore.entityId ?? null)
  const selectedEntity = computed<Entity | null>(() => entityStore.entity ?? null)

  const sidebarIsOpen = computed<boolean>(() => !!selectedEntityId.value)

  const setSelectedEntityId = (id: string | null) => {
    const prevPropertyId = selectedFileField.value?.field.propertyId
    entityStore.setEntityId(id ?? undefined)

    // We didn't have a file field selected, so just clear the selection
    if (!prevPropertyId) {
      selectedFileField.value = null
      return
    }

    // We did have a file field selected, so we have to try and select a matching one on new entity
    const field = entityStore.fileFields.find((f) => f.propertyId === prevPropertyId)
    selectedFileField.value = field ? { field } : null
  }

  const setSelectedFileField = (
    field: Field<'file' | 'file_collection'>,
    groundingField?: Field,
  ) => {
    selectedFileField.value = { field, groundingField }
  }

  const clearSelectedFileField = () => {
    selectedFileField.value = null
  }

  const reset = () => {
    selectedFileField.value = null
    entityStore.setEntityId(undefined)
  }

  return {
    clearSelectedFileField,
    reset,
    selectedEntity,
    selectedEntityId,
    selectedFileField,
    setSelectedEntityId,
    setSelectedFileField,
    sidebarIsOpen,
  }
})
